import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchCornerToCornerDetailByTickerAndCandlestick } from '../backend/fetchs/corner_to_corner';
import { CornerToCorner } from '../backend/interfaces/corner_to_corner';
import DateCell from '../components/DateCell';
import DirectionCell from '../components/DirectionCell';
import IntervalCell from '../components/IntervalCell';

const CornerToCornerDetailPage: React.FC = () => {
  const { id_ticker, id_candlestick } = useParams<{ id_ticker: string, id_candlestick: string }>();
  const [data, setData] = useState<CornerToCorner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id_ticker || !id_candlestick) return;
      
      try {
        setLoading(true);
        const result = await fetchCornerToCornerDetailByTickerAndCandlestick(parseInt(id_ticker), parseInt(id_candlestick));
        setData(result.data);
        setError(null);
      } catch (err) {
        setError('Error fetching corner to corner detail');
        console.error('Error fetching corner to corner detail:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id_ticker, id_candlestick]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center">
        <div className="text-red-500 text-xl mb-4">{error || 'Corner to corner not found'}</div>
        <Link
          to="/corner-to-corner"
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Back to Corner to Corner Alerts
        </Link>
      </div>
    );
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 8,
    }).format(price);
  };

  const formatVolume = (volume: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(volume);
  };

  const formatInterval = (interval: string) => {
    return interval === '1M' ? '1 month' : interval === '3M' ? '3 months' : interval === '6M' ? '6 months' : interval === '1y' ? '1 year' : interval;
  };

  const getExplanationItems = () => {

    const isLong = data[0].side.description === 'LONG';
    
    const items = [
      {
        text: `RSI is ${isLong ? 'below' : 'above'} ${data[0].indicators.rsi.threshold} points`,
        check: isLong ? data[0].indicators.rsi.value < data[0].indicators.rsi.threshold : data[0].indicators.rsi.value > data[0].indicators.rsi.threshold,
      },
    ];

    for (const item of data) {
      items.push({
        text: `Price ${formatPrice(item.indicators.price)} is close to the ${formatInterval(item.indicators.level.interval)} level of ${formatPrice(item.indicators.level.price)} (≈ ${item.indicators.level.threshold * 100}%)`,
        check: item.indicators.price > item.indicators.level.min_price && item.indicators.price < item.indicators.level.max_price,
      });
    }

    return items;
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Corner to Corner #{data[0].id}</h1>
        </div>
        
        {/* Main Info */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-sm font-medium text-gray-500">Ticker</h2>
              <p className="text-2xl font-bold text-gray-900">{data[0].ticker.description}</p>
            </div>
            <div>
              <h2 className="text-sm font-medium text-gray-500">Trend Confirmation</h2>
              <DirectionCell direction={data[0].side.description} />
            </div>
            <div>
              <h2 className="text-sm font-medium text-gray-500">Confirmation Date</h2>
              <DateCell timestamp={data[0].created_at} blackMode={true} />
            </div>
            <div>
              <h2 className="text-sm font-medium text-gray-500">Interval</h2>
              <IntervalCell interval={data[0].candlestick.interval.description} />
            </div>
          </div>
        </div>
      </div>

      {/* Explanation */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Explanation</h2>
        <div className="space-y-3">
          {getExplanationItems().map((item, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                item.check ? 'bg-green-100' : 'bg-red-100'
              }`}>
                {item.check ? (
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              <span className={`text-md ${item.check ? 'text-green-700' : 'text-red-700'}`}>
                {item.text}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Indicators Table */}
      {data.map((item, index) => (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Level {index + 1}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500">Level Min Price</h3>
                <p className="text-lg font-semibold text-gray-900">{formatPrice(item.indicators.level.min_price)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500">Level Price</h3>
                <p className="text-lg font-semibold text-gray-900">{formatPrice(item.indicators.level.price)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500">Level Max Price</h3>
                <p className="text-lg font-semibold text-gray-900">{formatPrice(item.indicators.level.max_price)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500">Level Threshold</h3>
                <p className="text-lg font-semibold text-gray-900">{item.indicators.level.threshold * 100}%</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500">Price</h3>
                <p className="text-lg font-semibold text-gray-900">{formatPrice(item.indicators.price)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500">Level Interval</h3>
                <p className="text-lg font-semibold text-gray-900">
                    {formatInterval(item.indicators.level.interval)}
                </p>
            </div>
            </div>
        </div>
      ))}



      {/* Candlestick Information */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Candlestick Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Open Date</h3>
              <DateCell timestamp={data[0].candlestick.open_time / 1000} blackMode={true} />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Close Date</h3>
              <DateCell timestamp={data[0].candlestick.close_time / 1000} blackMode={true} />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Volume</h3>
              <p className="text-lg font-semibold text-gray-900">{formatVolume(data[0].candlestick.volume)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Number of Trades</h3>
              <p className="text-lg font-semibold text-gray-900">
                {data[0].candlestick.number_of_trades.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Open</h3>
              <p className="text-lg font-semibold text-gray-900">{formatPrice(data[0].candlestick.open)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Close</h3>
              <p className="text-lg font-semibold text-gray-900">{formatPrice(data[0].candlestick.close)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">High</h3>
              <p className="text-lg font-semibold text-gray-900">{formatPrice(data[0].candlestick.high)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Low</h3>
              <p className="text-lg font-semibold text-gray-900">{formatPrice(data[0].candlestick.low)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CornerToCornerDetailPage; 